import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useContext, useEffect, useState } from "react";
import { ConfigContext, OrderDataContext, ReturnOrderContext } from "@/Layout";
import { useCalculateReturn } from "@/api/calculateReturn";
import { useCreateReturn } from "@/api/createReturn";
import { usePrepaymentStatus } from "@/api/prepaymentStatus";
import { AnimatedCurrency } from "@/components/AnimatedCurrency/AnimatedCurrency";
import { CountDown } from "@/components/CountDown/CountDown";
import ImageWithFallback from "@/components/ImageWithFallback/ImageWithFallback";
import { InfoIcon } from "@/components/InfoIcon/InfoIcon";
import LoadSpinner from "@/components/LoadSpinner/LoadSpinner";
import { Modal } from "@/components/Modal/Modal";
import NextButton from "@/components/NextButton/NextButton";
import { RefundMethod } from "@/components/RefundMethod/RefundMethod";
import { ReturnContainer } from "@/components/ReturnContainer/ReturnContainer";
import { cn } from "@/utils/utils";
import {
  formatCurrency,
  getErrorMessage,
  shouldShowRefundMethod,
} from "@/utils/utils";

export const Route = createFileRoute("/confirm")({
  component: ConfirmPage,
});

function ConfirmPage() {
  const configData = useContext(ConfigContext),
    { returnOrder } = useContext(ReturnOrderContext),
    { orderData } = useContext(OrderDataContext),
    { data: calculatedReturn, isPending: isCalculatingReturn } =
      useCalculateReturn({ enabled: true }),
    {
      mutate: createReturn,
      data: createdReturn,
      isSuccess: isCreateReturnSuccess,
      isPending: isCreateReturnPending,
      error: createReturnError,
    } = useCreateReturn(),
    [isReturnWithPrepaymentPollStarted, setIsReturnWithPrepaymentPollStarted] =
      useState(false),
    [isReturnWithPrepaymentPollEnded, setIsReturnWithPrepaymentPollEnded] =
      useState(false);

  const returnRequiresPayment =
    createdReturn !== undefined && "invoice_url" in createdReturn;

  const { data: prepaymentStatus } = usePrepaymentStatus(
      returnRequiresPayment ? createdReturn?.check_id : "",
      isReturnWithPrepaymentPollStarted && !isReturnWithPrepaymentPollEnded
    ),
    navigate = useNavigate();

  useEffect(() => {
    if (!returnRequiresPayment) return;
    if (isCreateReturnSuccess) {
      window.open(
        createdReturn.invoice_url,
        "_blank",
        "location=yes,height=570,width=520,scrollbars=yes,status=yes"
      );
    }
  }, [isCreateReturnSuccess, createdReturn, returnRequiresPayment]);

  useEffect(() => {
    if (returnRequiresPayment) {
      setIsReturnWithPrepaymentPollStarted(true);
    }
  }, [isCreateReturnSuccess, returnRequiresPayment]);

  useEffect(() => {
    if (prepaymentStatus?.status === "COMPLETED") {
      setIsReturnWithPrepaymentPollEnded(true);
    }
  }, [prepaymentStatus]);

  if (!returnOrder || !orderData || !configData)
    return navigate({
      to: "/",
      search: (prev) => prev,
    });

  if (
    (isCreateReturnSuccess && !returnRequiresPayment) ||
    prepaymentStatus?.status == "COMPLETED"
  )
    navigate({
      to: "/return-instruction",
      search: (prev) => prev,
    });

  const returnedItems = orderData.line_items.filter((item) =>
    returnOrder.line_items.some((returnItem) => returnItem.id === item.id)
  );

  const orderItemMap = new Map(
    orderData.line_items.map((item) => [item.id, item])
  );

  const returnContainsVariantExchanges = returnOrder.line_items.some(
    (item) => item.exchange_variant_id
  );
  const returnContainsUpsellExchanges =
    (returnOrder.upsell_exchanges?.length ?? 0) > 0;
  const returnContainsExchanges =
    returnContainsVariantExchanges || returnContainsUpsellExchanges;

  const exchangeBonus =
    calculatedReturn?.exchange_bonus.presentment_money.amount;

  const returnFee = returnOrder.refund_as_gift_card
    ? 0
    : calculatedReturn?.return_fee.presentment_money.amount;

  const storeCreditBonus = returnOrder.refund_as_gift_card
    ? (calculatedReturn?.refund_value.presentment_money.amount ?? 0) *
      (orderData.gift_card_value_multiplier - 1)
    : 0;

  const subtotalRefunded = Math.max(
    (calculatedReturn?.return_value.presentment_money.amount ?? 0) -
      (calculatedReturn?.exchange_value.presentment_money.amount ?? 0),
    0
  );

  const totalRefunded = returnOrder.refund_as_gift_card
    ? (calculatedReturn?.refund_value.presentment_money.amount ?? 0) +
      storeCreditBonus
    : (calculatedReturn?.refund_value.presentment_money.amount ?? 0) -
      (returnFee ?? 0);

  const currency =
    calculatedReturn?.refund_value.presentment_money.currency_code;

  return (
    <ReturnContainer>
      <div className="relative">
        {!calculatedReturn && (
          <div className="absolute top-0 left-0 w-full h-full flex justify-center items-center bg-white/50 z-20"></div>
        )}
        {returnRequiresPayment && (
          <Modal
            open={prepaymentStatus?.status === "PENDING"}
            title={configData.prepaid_exchange_payment_window_header}
            children={
              <div>
                <CountDown
                  timeInSeconds={600}
                  onEnd={() => window.location.reload()}
                />
                <p className="text-sm text-light mt-2">
                  {configData.prepaid_exchange_payment_window_message
                    .split(" ")
                    .slice(0, -1)
                    .join(" ") + " "}
                  <a href={createdReturn.invoice_url} target="_blank">
                    {
                      configData.prepaid_exchange_payment_window_message
                        .split(" ")
                        .slice(-1)[0]
                    }
                  </a>
                </p>
              </div>
            }
          />
        )}
        <h1 className="text-lg font-semibold">{configData.confirm_header}</h1>
        <hr className="mt-2 mb-10 sm:mb-8" />
        <div className="grid sm:flex gap-6 sm:gap-16">
          <div className="sm:min-w-[300px]">
            <div>
              <h2 className="font-medium text-base">
                {configData.items_returned_text}
              </h2>
              <hr className="my-2" />
              <ul className="flex flex-col gap-2">
                {returnedItems.map((item) => {
                  return (
                    <li key={item.id}>
                      <div className="flex gap-3">
                        <ImageWithFallback src={item.product_src} />
                        <div className="flex flex-col">
                          <span className="text-xs font-medium">
                            {item.product_title}
                          </span>
                          <span className="text-xs font-light">
                            {item.variant_title}
                          </span>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
              <hr className="my-2" />
            </div>
            {returnContainsExchanges && (
              <div className="mt-8">
                <h2 className="font-medium text-base">
                  {configData.exchange_items_received_text}
                </h2>
                <hr className="my-2" />
                <ul className="flex flex-col gap-2">
                  {returnOrder.line_items
                    .filter((item) => item.exchange_variant_id)
                    .map((item) => {
                      if (!item.exchange_variant_id) return null;
                      return (
                        <li key={item.id}>
                          <div className="flex gap-3">
                            <ImageWithFallback
                              src={
                                orderData.exchange_data?.variant_data[
                                  item.exchange_variant_id
                                ]?.src ??
                                orderItemMap.get(item.id)?.product_src ??
                                ""
                              }
                            />
                            <div className="flex flex-col">
                              <span className="text-xs font-medium">
                                {orderItemMap.get(item.id)?.product_title}
                              </span>
                              <span className="text-xs font-light">
                                {
                                  orderData.exchange_data?.variant_data[
                                    item.exchange_variant_id
                                  ]?.title
                                }
                              </span>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  {returnOrder?.upsell_exchanges?.map((item, index) => {
                    return (
                      <li key={index}>
                        <div className="flex gap-3">
                          <ImageWithFallback
                            src={item.variant_image ?? item.product_image ?? ""}
                          />
                          <div className="flex flex-col">
                            <span className="text-xs font-medium">
                              {item.product_title}
                            </span>
                            <span className="text-xs font-light">
                              {item.title}
                            </span>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
                <hr className="my-2" />
              </div>
            )}
          </div>
          <div>
            <div className="sm:min-w-[300px] mt-4 sm:mt-0">
              <h2 className="font-medium text-base">
                {configData.return_summary_text}
              </h2>
              <hr className="my-2" />
              <h3 className="text-xs font-medium mb-1">
                {configData.return_items_text} ({returnOrder.line_items.length})
              </h3>
              <ul className="flex flex-col gap-2">
                {returnedItems.map((item) => (
                  <li key={item.id}>
                    <div className="flex justify-between">
                      <span className="text-xs font-light w-4/5">
                        {item.product_title}{" "}
                        {<span className="font-bold">•</span>}{" "}
                        {item.variant_title}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="flex justify-between gap-4 items-center mt-2">
                <h3 className="font-medium text-xs">
                  {configData.total_return_value_text}
                </h3>
                <span className="font-medium text-xs">
                  {formatCurrency(
                    calculatedReturn?.return_value.presentment_money.amount ?? 0
                  )}
                </span>
              </div>
              <hr className="mt-2 mb-4" />
              <div>
                {returnContainsExchanges && (
                  <div>
                    <h3 className="font-medium text-xs mb-1">
                      {configData.exchange_items_text} (
                      {returnOrder.line_items.filter(
                        (item) => item.exchange_variant_id
                      ).length + (returnOrder.upsell_exchanges?.length ?? 0)}
                      )
                    </h3>
                    <ul className="flex flex-col gap-2">
                      {returnOrder.line_items
                        .filter((item) => item.exchange_variant_id)
                        .map((item) => {
                          if (!item.exchange_variant_id) return null;
                          return (
                            <li key={item.id}>
                              <div className="flex justify-between">
                                <span className="text-xs font-light w-4/5">
                                  {orderItemMap.get(item.id)?.product_title}
                                  <span className="font-bold"> • </span>
                                  {
                                    orderData.exchange_data?.variant_data[
                                      item.exchange_variant_id
                                    ]?.title
                                  }
                                </span>
                              </div>
                            </li>
                          );
                        })}
                      {returnOrder.upsell_exchanges?.map((item, index) => {
                        return (
                          <li key={index}>
                            <div className="flex justify-between">
                              <span className="text-xs font-light w-4/5">
                                {item.product_title}
                                <span className="font-bold"> • </span>
                                {item.title}
                              </span>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                    <div className="flex justify-between gap-4 items-center mt-2">
                      <h3 className="font-medium text-xs">
                        {configData.total_exchange_value_text}
                      </h3>
                      <span className="font-medium text-xs">
                        {formatCurrency(
                          calculatedReturn?.exchange_value.presentment_money
                            .amount ?? 0
                        )}
                      </span>
                    </div>
                    <hr className="mt-2 mb-4" />
                  </div>
                )}
              </div>
              {((calculatedReturn?.return_fee.presentment_money.amount ?? 0) >
                0 ||
                orderData.gift_card_value_multiplier) &&
                (!returnContainsExchanges ||
                  !orderData.exchange_data?.prepaid) && (
                  <div className="flex justify-between gap-4 items-center my-2">
                    <h3 className="font-medium text-xs">
                      {configData.subtotal_refunded_text}
                    </h3>
                    <span className="font-medium text-xs">
                      {formatCurrency(subtotalRefunded)}
                    </span>
                  </div>
                )}
              <div className="flex flex-col gap-2">
                {(calculatedReturn?.return_fee.presentment_money.amount ?? 0) >
                  0 && (
                  <div className="flex justify-between gap-4 items-center">
                    <span className="text-xs font-light">
                      {configData.return_fee_text}
                    </span>
                    <span className="text-xs font-light">
                      {formatCurrency(returnFee ?? 0)}
                    </span>
                  </div>
                )}
                {shouldShowRefundMethod(
                  returnOrder,
                  orderData,
                  calculatedReturn
                ) &&
                  totalRefunded > 0 && (
                    <div className="flex justify-between gap-4 items-center">
                      <span className="text-xs font-light">
                        {configData.gift_card_bonus_text}
                      </span>
                      <span className="text-xs font-light">
                        {formatCurrency(storeCreditBonus)}
                      </span>
                    </div>
                  )}
                {(exchangeBonus ?? 0) > 0 && (
                  <div className="flex justify-between gap-4 items-center">
                    <span className="text-xs font-light">
                      {configData?.exchange_bonus_text ?? "Exchange bonus"}
                    </span>
                    <span className="text-xs font-light">
                      {formatCurrency(exchangeBonus ?? 0)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between gap-4 items-center -mb-1">
                  <h3 className="text-base font-medium">
                    {configData.expected_refund_text}
                  </h3>
                  <span className="text-base font-medium">
                    <AnimatedCurrency
                      value={totalRefunded}
                      currency={currency}
                    />
                  </span>
                </div>
                <hr />
                {calculatedReturn?.requires_payment && (
                  <div>
                    <div className="flex justify-between gap-4 items-center -mt-1">
                      <h3 className="text-base font-medium">
                        {configData.prepaid_exchange_total_text}
                      </h3>
                      <span className="text-base font-medium">
                        {formatCurrency(
                          calculatedReturn.required_payment.presentment_money
                            .amount,
                          calculatedReturn.required_payment.presentment_money
                            .currency_code
                        )}
                      </span>
                    </div>
                    <hr className="my-2" />
                  </div>
                )}
                {orderData.exchange_data?.prepaid &&
                  returnContainsExchanges && (
                    <div
                      className={cn(
                        "flex items-center gap-2 max-w-96 bg-gray-100",
                        "p-2 rounded-sm border-[rgb(235, 235, 235)] border"
                      )}
                    >
                      <div>
                        <InfoIcon />
                      </div>
                      <i className="text-xs font-extralight whitespace-pre-line">
                        {configData.prepaid_exchange_note}
                      </i>
                    </div>
                  )}
                <div className="min-h-9">
                  {configData.return_fees_may_apply_text &&
                    !(
                      (calculatedReturn?.return_fee.presentment_money.amount ??
                        0) > 0
                    ) &&
                    !returnOrder.refund_as_gift_card &&
                    totalRefunded > 0 && (
                      <div
                        className={cn(
                          "flex items-center gap-2 max-w-96 bg-gray-100 p-2",
                          "rounded-sm border-[rgb(235, 235, 235)] border"
                        )}
                      >
                        <div>
                          <InfoIcon />
                        </div>
                        <i className="text-xs font-extralight">
                          {configData.return_fees_may_apply_text}
                        </i>
                      </div>
                    )}
                </div>
              </div>
            </div>
            {shouldShowRefundMethod(
              returnOrder,
              orderData,
              calculatedReturn
            ) && (
              <div className="mt-8">
                <h3 className="font-medium text-base">
                  {configData.refund_method_header}
                </h3>
                <hr className="my-2" />
                <RefundMethod />
                <hr className="my-2" />
              </div>
            )}
            <div className="mt-8">
              <NextButton
                text={
                  returnContainsExchanges && orderData.exchange_data?.prepaid
                    ? configData.confirm_button_prepaid_exchange_message
                    : configData.confirm_button_message
                }
                buttonDisabled={
                  (returnOrder.refund_as_gift_card == undefined &&
                    shouldShowRefundMethod(
                      returnOrder,
                      orderData,
                      calculatedReturn
                    )) ||
                  isCreateReturnSuccess ||
                  isCalculatingReturn
                }
                onClick={() => {
                  if (isCreateReturnPending || isCreateReturnSuccess) return;
                  createReturn(returnOrder);
                }}
                buttonColor={configData?.button_color}
              />
              <div className="min-h-6 justify-center flex mt-2">
                {isCreateReturnPending && <LoadSpinner />}
                {isCalculatingReturn && <LoadSpinner />}
                {createReturnError && (
                  <span className="text-red-500 text-sm">
                    {getErrorMessage(createReturnError, configData)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ReturnContainer>
  );
}
